import { ArrowDrawIcon } from "@images/svgIcons/arrowDrawIcon"
import { BoxSubscriptionDetailsEntity } from "@modules/boxSubscription/entities/boxSubscriptionDetailsEntity"
import {
  CouponInputFormik,
  couponYupValidation,
  DurationType,
} from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { useBoxSubscriptionActions } from "@modules/boxSubscription/hooks/useBoxSubscription"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { getFormattedCouponReduction } from "@modules/common/couponObject/utils/couponObjectUtils"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { LoggerType } from "@modules/utils/loggerUtils"
import dayjs from "dayjs"
import { useFormik } from "formik"
import { ArrowLeft } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTheme } from "styled-components"

type Props = {
  memberId: string
  subscription: BoxSubscriptionDetailsEntity
  setIsOfferDiscountVisible: React.Dispatch<React.SetStateAction<boolean>>
  className?: string
} & React.ComponentProps<"div">

export const MemberOfferDiscountForm: React.FC<Props> = ({
  memberId,
  subscription,
  setIsOfferDiscountVisible,
  className,
  ...props
}) => {
  const defaultClassName = "flex flex-column w-full pb-4"
  const theme = useTheme()
  const toast = useToast()
  const language = useLanguage()

  const [isDisableMonths, setIsDisableMonths] = useState(false)
  const [isResumeVisible, setIsResumeVisible] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { isPending, applyDiscount, removeDiscount } = useBoxSubscriptionActions(memberId, subscription.id)

  const discountTypesOptions = useMemo(() => {
    return [
      { value: CouponType.PERCENT, label: word("box.member.table.offer.discount.percentInputLabel") },
      { value: CouponType.DIRECT, label: word("box.member.table.offer.discount.directInputLabel") },
    ]
  }, [language])

  const durationTypesOptions = useMemo(() => {
    return [
      { value: DurationType.MONTHLY, label: word("box.member.table.offer.discount.monthlyInputLabel") },
      { value: DurationType.UNLIMITED, label: word("box.member.table.offer.discount.unlimitedInputLabel") },
    ]
  }, [language])

  const formik = useFormik<CouponInputFormik>({
    initialValues: { type: CouponType.PERCENT, value: 0, durationType: DurationType.MONTHLY, months: 1 },
    validationSchema: couponYupValidation({ priceOffer: subscription.priceObject?.value || 0 }),
    onSubmit: async () => {
      try {
        setIsSubmitting(true)
        await applyDiscount(formik.values)
        toast.show({
          severity: "success",
          summary: word("box.member.table.offer.discount.toastSuccess"),
        })
        setIsResumeVisible(true)
      } catch (e: any) {
        console.error(LoggerType.Service + "update member", e)
        toast.show({ severity: "error", summary: word("box.member.table.offer.discount.toastError") })
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  const priceDiscount = useMemo(() => {
    if (formik.values.value > 0 && subscription.priceObject) {
      let priceAfterDiscount = subscription.priceObject.value
      if (formik.values.type === CouponType.PERCENT) {
        const discountPercentage = formik.values.value / 100
        priceAfterDiscount = priceAfterDiscount - priceAfterDiscount * discountPercentage
      } else if (formik.values.type === CouponType.DIRECT) {
        priceAfterDiscount = priceAfterDiscount - formik.values.value
      }
      return priceAfterDiscount
    } else return 0
  }, [formik.values.value, formik.values.type])

  const handleOnSubmit = async () => {
    await formik.submitForm()
  }

  const handleOnEdit = async () => {
    setIsResumeVisible(false)
  }

  const handleOnDelete = async () => {
    if (subscription.coupon) {
      await removeDiscount()
        .then(() => {
          setIsResumeVisible(false)
          formik.resetForm()
          toast.show({
            severity: "success",
            summary: word("box.member.table.offer.discount.toastDeleteSuccess"),
          })
        })
        .catch((e: any) => {
          console.error(LoggerType.Service + "remove discount", e)
          toast.show({
            severity: "error",
            summary: word("box.member.table.offer.discount.toastDeleteError"),
          })
        })
    }
  }

  useEffect(() => {
    if (formik.values.durationType === DurationType.UNLIMITED) {
      setIsDisableMonths(true)
      formik.setFieldValue("months", null)
    } else {
      formik.setFieldValue("months", 1)
      setIsDisableMonths(false)
    }
  }, [formik.values.durationType])

  useEffect(() => {
    if (subscription) {
      const coupon = subscription.getCoupon()
      formik.setFieldValue("type", coupon.type)
      formik.setFieldValue("value", coupon.value)
      formik.setFieldValue("durationType", coupon.durationType)
      formik.setFieldValue("months", coupon.months)

      if (subscription.coupon) setIsResumeVisible(true)
    }
  }, [subscription])

  return (
    <div className={className ?? defaultClassName} {...props}>
      {/* HEADER */}
      <div className="flex flex-column px-5 py-4 gap-4">
        <div className="flex">
          <ArrowLeft
            strokeWidth={2.5}
            size={26}
            onClick={() => setIsOfferDiscountVisible(false)}
            className="cursor-pointer"
          />
          <div className="flex flex-column flex-grow-1 justify-content-center align-items-center">
            <HUText fontStyle="TS" color={theme.mainWhite}>
              {word("box.member.table.offer.discount.title")}
            </HUText>
            <HUText fontStyle="BM" color={theme.neutralColor700}>
              {subscription.offer.name}
            </HUText>
          </div>
        </div>
      </div>
      {isResumeVisible ? (
        // RESUME
        <div className="flex flex-column mt-4 gap-4">
          <div className="flex flex-column gap-1">
            <HUText fontStyle="LS" color={theme.mainWhite}>
              {word("box.member.table.offer.discount.supensionTypeLabel")}
            </HUText>
            {subscription.priceObject && (
              <HUText fontStyle="TM" color={theme.neutralColor700} className="mt-2">
                {getFormattedCouponReduction(
                  formik.values.type,
                  formik.values.value,
                  language,
                  subscription.priceObject.currency,
                )}{" "}
                {word("box.member.table.offer.discount.off")}
              </HUText>
            )}
          </div>
          <div className="flex w-full justify-content-between mt-2 mb-4 px-4">
            <div className="flex flex-column align-items-center">
              <HUText fontStyle="LS" color={theme.neutralColor700} className="mt-2">
                {word("box.member.table.offer.discount.offerPriceLabel")}
              </HUText>
              <HUText
                fontStyle="TM"
                color={theme.error}
                className={` mt-2`}
                style={{ textDecorationThickness: "1px", textDecorationLine: "line-through" }}
              >
                {formatCurrency(subscription.priceObject?.value ?? 0, subscription.priceObject?.currency, language)}
              </HUText>
            </div>

            <div className="flex align-items-end">
              <ArrowDrawIcon color={theme.neutralColor700} />
            </div>

            <div className="flex flex-column align-items-center">
              <HUText fontStyle="LS" color={theme.neutralColor700} className="mt-2">
                {word("box.member.table.offer.discount.offerPriceAfterDiscountLabel")}
              </HUText>
              <HUText fontStyle="TM" color={theme.sucess} className="mt-2">
                {formatCurrency(priceDiscount, subscription.priceObject?.currency, language)}
              </HUText>
            </div>
          </div>
          <div className="flex flex-column">
            <HUText fontStyle="TM" color={theme.mainWhite}>
              {subscription.coupon?.duration !== null
                ? word("box.member.table.offer.resume.defineDuration", {
                    count: subscription.coupon?.duration,
                  })
                : word("box.member.table.offer.resume.unlimitedDuration")}
            </HUText>
            <HUText fontStyle="LM" color={theme.neutralColor700} className="mt-2">
              {` ${word("box.member.table.offer.resume.unlimitedDurationStart")} ${dayjs(
                subscription.coupon?.createdAt,
              ).format("LL")}`}
            </HUText>
          </div>

          <div className="flex w-full gap-2 mt-8">
            <HUButton
              type="Right_Icon"
              size="M"
              colorType="Quaternary"
              text={word("global.delete")}
              onClick={handleOnDelete}
              className="w-6"
              disabled={isPending}
            />
            <HUButton
              type="Right_Icon"
              size="M"
              colorType="Primary"
              text={word("global.edit")}
              onClick={handleOnEdit}
              className="w-6"
              disabled={isPending}
            />
          </div>
        </div>
      ) : (
        // FORMIK
        <div className="flex flex-column">
          <FormikInput
            flex
            formikStyle={{ flexGrow: 1 }}
            label={word("box.member.table.offer.discount.supensionTypeLabel")}
            type="dropdown"
            getFieldProps={formik.getFieldProps}
            isInvalid={isFormFieldInValid("type", formik)}
            name="type"
            error={getFormErrorMessage("type", formik)}
            dropdownProps={{
              value: formik.values.type,
              options: discountTypesOptions,
            }}
            setFieldValue={formik.setFieldValue}
            disabled={isPending}
          />
          <div className="flex flex-grow">
            <FormikInput
              flex
              formikStyle={{ flexGrow: 1 }}
              type="number"
              getFieldProps={formik.getFieldProps}
              isInvalid={isFormFieldInValid("value", formik)}
              name="value"
              error={getFormErrorMessage("value", formik)}
              numberProps={{
                min: 0,
                max: formik.values.type === CouponType.PERCENT ? 100 : subscription.priceObject?.value,
                step: 0.1,
                minFractionDigits: 0,
                maxFractionDigits: 2,
              }}
              setFieldValue={formik.setFieldValue}
              disabled={isPending}
            />
            {formik.values.type === CouponType.PERCENT && (
              <HUText
                fontStyle="TS"
                color={theme.neutralColor700}
                className="flex justify-content-center align-items-center ml-2 pb-3 "
              >
                %
              </HUText>
            )}
          </div>
          <div className="flex w-full justify-content-between mt-2 mb-4 px-4">
            <div className="flex flex-column align-items-center">
              <HUText fontStyle="LS" color={theme.neutralColor700} className="mt-2">
                {word("box.member.table.offer.discount.offerPriceLabel")}
              </HUText>
              <HUText
                fontStyle="TM"
                color={priceDiscount > 0 ? theme.error : theme.neutralColor700}
                className={` mt-2`}
                style={priceDiscount > 0 ? { textDecorationThickness: "1px", textDecorationLine: "line-through" } : {}}
              >
                {formatCurrency(subscription.priceObject?.value ?? 0, subscription.priceObject?.currency, language)}
              </HUText>
            </div>
            {priceDiscount > 0 && (
              <div className="flex align-items-end">
                <ArrowDrawIcon color={theme.neutralColor700} />
              </div>
            )}
            <div className="flex flex-column align-items-center">
              <HUText fontStyle="LS" color={theme.neutralColor700} className="mt-2">
                {word("box.member.table.offer.discount.offerPriceAfterDiscountLabel")}
              </HUText>
              <HUText fontStyle="TM" color={priceDiscount > 0 ? theme.sucess : theme.neutralColor700} className="mt-2">
                {priceDiscount >= 0
                  ? formatCurrency(priceDiscount, subscription.priceObject?.currency, language)
                  : "--"}
              </HUText>
            </div>
          </div>

          <FormikInput
            flex
            formikStyle={{ flexGrow: 1 }}
            label={word("box.member.table.offer.discount.durationTypeLabel")}
            type="dropdown"
            getFieldProps={formik.getFieldProps}
            isInvalid={isFormFieldInValid("durationType", formik)}
            name="durationType"
            error={getFormErrorMessage("durationType", formik)}
            dropdownProps={{
              value: formik.values.durationType,
              options: durationTypesOptions,
            }}
            setFieldValue={formik.setFieldValue}
            disabled={isPending}
          />
          <FormikInput
            flex
            formikStyle={{ flexGrow: 1 }}
            type="number"
            getFieldProps={formik.getFieldProps}
            isInvalid={isFormFieldInValid("months", formik)}
            name="months"
            error={getFormErrorMessage("months", formik)}
            numberProps={{
              min: 1,
              maxFractionDigits: 0,
            }}
            setFieldValue={formik.setFieldValue}
            disabled={isDisableMonths || isPending}
          />

          <HUButton
            type="Right_Icon"
            size="M"
            colorType="Primary"
            text={word("global.save")}
            onClick={handleOnSubmit}
            disabled={isPending || isSubmitting || Object.keys(formik.errors).length > 0}
            loading={isPending || isSubmitting}
            className="w-full"
          />
        </div>
      )}
    </div>
  )
}
